import React from 'react';
import type { Campaign, CampaignEditRequest } from '@discovr/types';
import { DELIVERABLE_REGISTRY, resolvePlatform, isOfferedOnPlatform, hasReelDeliverable } from '@discovr/types';
import { useCampaignCapabilitiesStore, invalidateLandedCampaignFieldChange, campaignKeys } from '@discovr/features';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, useAuth } from '@discovr/common';
import { CardShell, Section, Field, PlatformMark, titleCase, formatRegion } from './campaignDetailsParts';
import { ChipMultiSelect } from './ChipMultiSelect';
import { PrimaryGoalSelect, ContentTypeCards, CategoryTiles } from './editFieldControls';
import { CommentCard, SegmentedControl, ReferencesField } from './editFormParts';
import {
  type FormState,
  stateFromCampaign,
  arraysEqual,
  computeDirtyPaths,
  buildPayload,
  getFieldValue,
  withNumericFallback,
} from './editFormState';
import { EditableList } from './EditableList';
import { ProductRowsEditor } from './ProductRowsEditor';
import { CampaignEditAudioField } from './CampaignEditAudioField';
import { FieldCommentDot } from './FieldCommentDot';
import {
  USAGE_RIGHTS_MIN_DAYS,
  USAGE_RIGHTS_MAX_DAYS,
  isUsageRightsDaysValid,
  getUsageRightsError,
  invalidBorder,
} from './CampaignEditForm.helpers';
import { describeChange, goToField, SHEET_CLOSE_ANIMATION_MS } from './editRequestFormat';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import {
  SquaresFour,
  FileText,
  NotePencil,
  UserFocus,
  UsersThree,
  VideoCamera,
  ChatCircle,
  Info,
  Lock,
  ShoppingBag,
} from '../../design-system/icons';
import { SimpleTooltip } from '../../components/ui/tooltip';
import {
  PAYOUT_MODEL_OPTIONS,
  CREATOR_CATEGORY_OPTIONS,
  TIER_OPTIONS,
  REGION_OPTIONS,
  LANGUAGE_OPTIONS,
} from './editFieldOptions';


// ── Label row (plain-input fields only — ChipMultiSelect renders its own via
//    labelExtra) ─────────────────────────────────────────────────────────

interface FieldLabelRowProps {
  label: string;
  path: string;
  dirty: boolean;
  pending?: boolean;
  authorName: string;
  comment: string;
  onCommentChange: (value: string) => void;
}

function FieldLabelRow({ label, path, dirty, pending, authorName, comment, onCommentChange }: FieldLabelRowProps) {
  return (
    // min-h matches the 24px comment marker so a field's input doesn't shift
    // down when it goes dirty — keeps paired grid fields aligned either way.
    <div className="mb-1.5 flex min-h-6 items-center gap-1.5 text-body-sm-regular text-muted-foreground">
      <span>{label}</span>
      {pending && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-caption-medium text-amber-700">
          Pending review
        </span>
      )}
      <FieldCommentDot
        active={dirty}
        label={label}
        path={path}
        authorName={authorName}
        comment={comment}
        onCommentChange={onCommentChange}
      />
    </div>
  );
}

// Explicit labels keep the stored value canonical lowercase while the chips
// render nicely: regions as "USA"/"India" (via formatRegion, not titleCase's
// "Usa") and "non-binary" as "Non-binary". Gender fields pass `allOption`
// ("All" = empty selection, no filter), mirroring the create-campaign wizard.
const REGION_CHOICES: { value: string; label: string }[] = REGION_OPTIONS.map((r) => ({
  value: r,
  label: formatRegion(r),
}));

const GENDER_CHOICES: { value: string; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-binary' },
];

// ── Component ────────────────────────────────────────────────────────────

interface CampaignEditFormProps {
  campaign: Campaign;
  /** When present, every pending FieldChange pre-fills the form so the brand
   *  sees their in-flight requested value, not the live campaign value.
   *  Approved changes are excluded — those are already merged into the campaign. */
  editRequest?: CampaignEditRequest | null;
  onDone: () => void;
}

export function CampaignEditForm({ campaign, editRequest, onDone }: CampaignEditFormProps) {
  const { submitCampaignEditRequest, editCampaignDirectly } = useCampaignCapabilitiesStore();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const authorName = user?.displayName || user?.email || 'You';
  // Pending-only: approved changes are already applied to `campaign`; rejected
  // ones are dead — neither belongs in the form's initial state.
  const pendingOverrides = React.useMemo(() => {
    const map = new Map<string, unknown>();
    for (const c of editRequest?.changes ?? []) {
      if (c.status === 'pending') map.set(c.field_path, c.new_value);
    }
    return map;
  }, [editRequest]);
  const pendingPaths = React.useMemo(() => new Set(pendingOverrides.keys()), [pendingOverrides]);
  const initial = React.useMemo(() => stateFromCampaign(campaign, pendingOverrides), [campaign, pendingOverrides]);
  // Lazy initializer — only runs on mount, so `form` won't pick up a later
  // `initial` on its own (see the resync effect below).
  const [form, setForm] = React.useState<FormState>(initial);
  const [comments, setComments] = React.useState<Record<string, string>>({});
  // Notes are drafts until the request is submitted, so there's no server
  // timestamp to show yet — stamp them as they're written so the Comments
  // sheet can age them the same way the review panel ages posted ones.
  const [commentedAt, setCommentedAt] = React.useState<Record<string, string>>({});
  const [commentsOpen, setCommentsOpen] = React.useState(false);
  const [editingPath, setEditingPath] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [lockError, setLockError] = React.useState<string | null>(null);
  // Duration is only enforced once the user tries to Save (like the create
  // wizard defers to Continue) — no red border while they're still typing.
  const [usageRightsTried, setUsageRightsTried] = React.useState(false);
  const [usageRightsHighlighted, setUsageRightsHighlighted] = React.useState(false);
  const usageRightsRef = React.useRef<HTMLDivElement>(null);
  const highlightTimer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(highlightTimer.current), []);

  // `set` is the single writer for `form`; flip this on the first edit so the
  // resync effect below never clobbers in-progress typing.
  const isPristineRef = React.useRef(true);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    isPristineRef.current = false;
    setForm((f) => ({ ...f, [key]: value }));
  };

  // `editRequest` loads via a separate async query and may be `undefined` on
  // mount. Since `form` seeds from `initial` only once, resync the first time
  // `editRequest` resolves to a real value — but only while the form is still
  // pristine, so an in-progress edit is never overwritten.
  const pendingEditRequestResync = React.useRef(editRequest === undefined);
  React.useEffect(() => {
    if (!pendingEditRequestResync.current || editRequest === undefined) return;
    pendingEditRequestResync.current = false;
    if (isPristineRef.current) setForm(initial);
  }, [editRequest, initial]);

  // `form` drives the inputs (may hold NaN for a cleared number box). `effective`
  // is what we diff and submit — emptied numbers revert to their default value.
  const effective = React.useMemo(() => withNumericFallback(form, initial), [form, initial]);
  const dirtyPaths = React.useMemo(() => computeDirtyPaths(initial, effective), [initial, effective]);
  const dirtySet = React.useMemo(() => new Set(dirtyPaths), [dirtyPaths]);
  const hasChanges = dirtyPaths.length > 0;
  // Which half of the shared `deliverables` field the user actually touched.
  const contentTypesDirty = !arraysEqual(initial.content_types, form.content_types);
  const addonsDirty = !arraysEqual(initial.addon_types, form.addon_types);
  // The sheet is a list of notes, not of changes: a change whose prompt was
  // skipped needs no note and doesn't belong here.
  const commentedPaths = React.useMemo(
    () => dirtyPaths.filter((p) => (comments[p] ?? '').trim().length > 0),
    [dirtyPaths, comments],
  );

  // Validate the duration only when the field was touched — a campaign saved under
  // the old 3-year cap stays Save-able if left alone.
  const usageRightsInvalid =
    form.usage_rights_enabled && dirtySet.has('usage_rights') && !isUsageRightsDaysValid(form.usage_rights_days);
  const showUsageRightsError = usageRightsTried && usageRightsInvalid;
  const usageRightsError = getUsageRightsError(form.usage_rights_days);
  const usageRightsCardClasses = `col-span-2 scroll-mt-24 rounded-lg border px-3 py-2.5 transition-all ${
    usageRightsHighlighted ? 'border-foreground ring-2 ring-foreground' : 'border-border'
  }`;

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    set('usage_rights_days', e.target.valueAsNumber);
  const handleUsageRightsToggle = (enabled: boolean) => set('usage_rights_enabled', enabled);

  // Send the user back to the duration field on a blocked Save (scroll + ring
  // flash), the React way: a ref for the scroll, state for the highlight.
  const revealUsageRights = () => {
    usageRightsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setUsageRightsHighlighted(true);
    window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => setUsageRightsHighlighted(false), 1600);
  };

  const renderUsageRightsHelp = () => {
    if (!form.usage_rights_enabled) return null;
    if (showUsageRightsError) {
      return <p className="mt-1.5 text-caption-regular text-destructive">{usageRightsError}</p>;
    }
    return (
      <p className="mt-1.5 text-caption-regular text-muted-foreground">
        Between {USAGE_RIGHTS_MIN_DAYS} days and 3 years (1095 days).
      </p>
    );
  };

  const submit = submitCampaignEditRequest ?? editCampaignDirectly;
  const submitLabel = 'Save changes';

  // Single writer for both note surfaces (the field marker and the sheet) so
  // the timestamp can't drift from the text. Clearing a note drops its stamp.
  const setComment = (path: string, text: string) => {
    setComments((c) => ({ ...c, [path]: text }));
    setCommentedAt((t) => {
      if (!text.trim()) {
        const { [path]: _dropped, ...rest } = t;
        return rest;
      }
      return { ...t, [path]: new Date().toISOString() };
    });
  };

  // `ownDirty` overrides which change arms this marker: Content types and
  // Add-ons are two controls behind the one `deliverables` field, so each needs
  // its own dirty flag rather than both prompting when either is touched.
  // `anchorPath` overrides only the DOM anchor id (keeping the comment key on
  // `path`) so the two don't both stamp a duplicate `id="field-deliverables"`.
  const commentDot = (path: string, label: string, ownDirty?: boolean, anchorPath: string = path) => (
    <FieldCommentDot
      active={ownDirty ?? dirtySet.has(path)}
      label={label}
      path={anchorPath}
      authorName={authorName}
      comment={comments[path] ?? ''}
      onCommentChange={(v) => setComment(path, v)}
    />
  );
  const labelRow = (path: string, label: string) => (
    <FieldLabelRow
      label={label}
      path={path}
      dirty={dirtySet.has(path)}
      pending={pendingPaths.has(path)}
      authorName={authorName}
      comment={comments[path] ?? ''}
      onCommentChange={(v) => setComment(path, v)}
    />
  );

  const handleSubmit = async () => {
    if (!submit || !hasChanges) return;
    if (usageRightsInvalid) {
      setUsageRightsTried(true);
      revealUsageRights();
      return;
    }
    setSubmitting(true);
    setLockError(null);
    try {
      const payload = buildPayload(effective, dirtySet, comments);
      await submit(payload);
      if (submitCampaignEditRequest) {
        // Brand submit — the change is pending review, not landed; refresh only
        // the detail + edit-request queries so the pending request shows.
        queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaign.id) });
        queryClient.invalidateQueries({ queryKey: campaignKeys.editRequestCurrent(campaign.id) });
        queryClient.invalidateQueries({ queryKey: campaignKeys.editRequestHistory(campaign.id) });
      } else {
        // Admin "edit myself" — applies immediately, so also refresh the
        // roster/product-consumer surfaces (see the helper's own doc).
        invalidateLandedCampaignFieldChange(queryClient, campaign.id);
      }
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.httpStatus === 409) {
        setLockError('Locked — a creator has confirmed. This campaign can no longer be edited.');
      } else {
        setLockError(err instanceof Error ? err.message : 'Failed to submit changes');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Normalised once (the stored value isn't always canonical) since every
  // lookup below — registry filters, isOfferedOnPlatform, gradients — keys off it.
  const platform = resolvePlatform(campaign.platform);

  // Campaign audio (Official Audio Compliance) only applies to Instagram
  // campaigns with a Reel deliverable selected — CampaignEditAudioField is
  // hidden outside that condition (mirrors NewCampaign.tsx's create-wizard
  // gate), but hiding a controlled field doesn't clear it. Without this,
  // deselecting the last Reel content type while editing would leave a stale
  // official_audio that still ships on Save.
  //
  // Writes via setForm directly, NOT `set()` — this is a system-derived
  // correction, not a user edit, so it must not flip isPristineRef. Doing so
  // would (a) permanently disarm the async editRequest resync above if it
  // resolves after this effect already ran, silently dropping pending-review
  // pre-fill, and (b) mark official_audio dirty for a field the user never
  // touched, with no comment-dot UI to explain why (CampaignEditAudioField is
  // hidden in this exact branch).
  React.useEffect(() => {
    if (platform === 'instagram' && hasReelDeliverable(form.content_types)) return;
    if (form.official_audio) setForm((f) => ({ ...f, official_audio: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, form.content_types]);

  // Feeds the selected-value chips; ContentTypeCards reads the registry itself
  // for the menu (it needs each type's glyph and sublabel too).
  const contentTypeOptions = Object.entries(DELIVERABLE_REGISTRY)
    .filter(([, meta]) => meta.category === 'main' && (meta.platform === platform || meta.platform === '*'))
    .map(([value, meta]) => ({ value, label: meta.label }));
  const addonTypeOptions = Object.entries(DELIVERABLE_REGISTRY)
    .filter(([id, meta]) => meta.category === 'addon' && id !== 'usage_rights' && (meta.platform === platform || meta.platform === '*'))
    .map(([value, meta]) => ({ value, label: meta.label }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-heading-h5 font-semibold text-foreground">Editing Campaign Details</h2>
          <p className="mt-1 text-body-sm-regular text-muted-foreground">
            Changes are sent to our ops team for review before they go live.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={onDone} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCommentsOpen(true)} className="gap-1.5">
            <ChatCircle size={14} />
            Comments
            {commentedPaths.length > 0 && (
              <span className="ml-0.5 flex size-4 items-center justify-center rounded-full bg-foreground text-[10px] text-background">
                {commentedPaths.length}
              </span>
            )}
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!hasChanges || submitting}>
            {submitting ? 'Submitting…' : hasChanges ? `${submitLabel} (${dirtyPaths.length})` : submitLabel}
          </Button>
        </div>
      </div>

      {lockError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
          {lockError}
        </div>
      )}

      {/* Same grid as the read view so entering edit mode doesn't shift cards. */}
      <div className="grid gap-6 lg:grid-cols-[81fr_83fr]">
        <CardShell icon={SquaresFour} title="Campaign Details">
          <div className="space-y-6">
            <Section icon={NotePencil} title="Campaign basics">
              <div className="col-span-2 min-w-0">
                {labelRow('name', 'Campaign name')}
                <Input
                  aria-label="Campaign name"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                />
              </div>
              <Field label="Platform">
                <div className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-muted px-3 py-1 text-body-sm-regular text-foreground shadow-xs">
                  <span className="flex items-center gap-2">
                    <PlatformMark platform={platform} />
                    {titleCase(platform)}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Lock size={14} />
                    <span className="text-caption-regular">Locked</span>
                  </span>
                </div>
              </Field>
              <div className="min-w-0">
                {labelRow('primary_goal', 'Primary goal')}
                <PrimaryGoalSelect value={form.primary_goal} onChange={(v) => set('primary_goal', v)} />
              </div>
              <div className="min-w-0">
                {labelRow('total_budget', 'Total budget')}
                <Input
                  type="number"
                  value={Number.isFinite(form.total_budget) ? form.total_budget : ''}
                  onChange={(e) => set('total_budget', e.target.valueAsNumber)}
                />
              </div>
              <div className="min-w-0">
                {labelRow('creator_preference.creator_count', 'Creator Count')}
                <Input
                  type="number"
                  value={Number.isFinite(form.creator_count) ? form.creator_count : ''}
                  onChange={(e) => set('creator_count', e.target.valueAsNumber)}
                />
              </div>
            </Section>

            <Section icon={UserFocus} title="Target Audience" divider>
              <div className="col-span-2 flex gap-4">
                <div className="w-28">
                  {labelRow('targeting.demographics.age_min', 'Age min')}
                  <Input type="number" value={Number.isFinite(form.age_min) ? form.age_min : ''} onChange={(e) => set('age_min', e.target.valueAsNumber)} />
                </div>
                <div className="w-28">
                  {labelRow('targeting.demographics.age_max', 'Age max')}
                  <Input type="number" value={Number.isFinite(form.age_max) ? form.age_max : ''} onChange={(e) => set('age_max', e.target.valueAsNumber)} />
                </div>
              </div>
              <ChipMultiSelect
                label="Gender"
                values={form.audience_genders}
                onChange={(v) => set('audience_genders', v)}
                options={GENDER_CHOICES}
                allOption
                labelExtra={commentDot('targeting.demographics.genders', 'Gender')}
              />
              <ChipMultiSelect
                label="Language"
                values={form.languages}
                onChange={(v) => set('languages', v)}
                options={LANGUAGE_OPTIONS}
                labelExtra={commentDot('targeting.demographics.languages', 'Language')}
              />
              <ChipMultiSelect
                label="Region"
                values={form.regions}
                onChange={(v) => set('regions', v)}
                options={REGION_CHOICES}
                labelExtra={commentDot('targeting.demographics.regions', 'Region')}
              />
            </Section>

            <Section icon={UsersThree} title="Target creators" divider>
              <ChipMultiSelect
                label="Creator tiers"
                values={form.creator_tiers}
                onChange={(v) => set('creator_tiers', v)}
                options={TIER_OPTIONS}
                labelExtra={commentDot('creator_preference.tiers', 'Creator tiers')}
              />
              <ChipMultiSelect
                label="Categories"
                values={form.categories}
                onChange={(v) => set('categories', v)}
                options={CREATOR_CATEGORY_OPTIONS}
                menuClassName="w-[368px] p-3"
                renderMenu={() => (
                  <CategoryTiles
                    options={CREATOR_CATEGORY_OPTIONS}
                    values={form.categories}
                    onChange={(v) => set('categories', v)}
                  />
                )}
                labelExtra={commentDot('creator_preference.categories', 'Categories')}
              />
              <ChipMultiSelect
                label="Creator Gender"
                values={form.creator_genders}
                onChange={(v) => set('creator_genders', v)}
                options={GENDER_CHOICES}
                allOption
                labelExtra={commentDot('creator_preference.creator_genders', 'Creator Gender')}
              />
              <div className="col-span-2 min-w-0">
                {labelRow('creator_preference.gender_notes', 'Creator gender notes')}
                <Textarea value={form.gender_notes} onChange={(e) => set('gender_notes', e.target.value)} rows={2} />
              </div>
            </Section>

            <Section icon={VideoCamera} title="Deliverables & terms" divider>
              <div className="col-span-2 min-w-0">
                {labelRow('payout_model', 'Payout model')}
                <SegmentedControl
                  options={PAYOUT_MODEL_OPTIONS}
                  value={form.payout_model}
                  onChange={(v) => set('payout_model', v)}
                />
              </div>
              <ChipMultiSelect
                label="Content types"
                values={form.content_types}
                onChange={(v) => set('content_types', v)}
                options={contentTypeOptions}
                menuClassName="w-[215px] p-3"
                renderMenu={() => (
                  <ContentTypeCards
                    platform={platform ?? ''}
                    values={form.content_types}
                    onChange={(v) => set('content_types', v)}
                  />
                )}
                labelExtra={commentDot('deliverables', 'Content types', contentTypesDirty)}
              />
              {/* Add-ons and usage rights are Instagram-only, gated via the
                  registry (isOfferedOnPlatform), not a hardcoded platform check. */}
              {addonTypeOptions.length > 0 && (
                <ChipMultiSelect
                  label="Add-ons"
                  values={form.addon_types}
                  onChange={(v) => set('addon_types', v)}
                  options={addonTypeOptions}
                  labelExtra={commentDot('deliverables', 'Add-ons', addonsDirty, 'deliverables.addons')}
                />
              )}
              {isOfferedOnPlatform('usage_rights', platform ?? '') && (
                <div ref={usageRightsRef} className={usageRightsCardClasses}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-1.5 text-body-sm-medium text-foreground">
                        Usage rights
                        <SimpleTooltip content="Grants rights to reuse this content for your paid ad campaigns.">
                          <button
                            type="button"
                            aria-label="About usage rights"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex shrink-0 cursor-help text-muted-foreground"
                          >
                            <Info size={14} />
                          </button>
                        </SimpleTooltip>
                        {commentDot('usage_rights', 'Usage rights')}
                      </div>
                      <div className="text-caption-regular text-muted-foreground">Repurpose creator content in your own ads.</div>
                    </div>
                    <div className="flex items-center gap-3">
                      {form.usage_rights_enabled && (
                        <Input
                          type="number"
                          min={USAGE_RIGHTS_MIN_DAYS}
                          max={USAGE_RIGHTS_MAX_DAYS}
                          aria-invalid={showUsageRightsError}
                          value={Number.isFinite(form.usage_rights_days) ? form.usage_rights_days : ''}
                          onChange={handleDurationChange}
                          className={`w-20 ${invalidBorder(showUsageRightsError)}`}
                        />
                      )}
                      <Switch checked={form.usage_rights_enabled} onCheckedChange={handleUsageRightsToggle} />
                    </div>
                  </div>
                  {renderUsageRightsHelp()}
                </div>
              )}
              {/* Campaign audio (Official Audio Compliance) — Instagram Reels
                  only (mirrors the create-wizard's gate — see
                  DeliverablesSection.tsx / NewCampaign.tsx): the BE can only
                  scrape a published Reel's audio, so the field stays hidden
                  until a Reel deliverable is actually selected here too. Set
                  via resolve-then-Verify and shipped through the same
                  reviewed Save as every other field. */}
              {platform === 'instagram' && hasReelDeliverable(form.content_types) && (
                <CampaignEditAudioField
                  value={form.official_audio}
                  onChange={(audio) => set('official_audio', audio)}
                  labelExtra={commentDot('official_audio', 'Campaign audio')}
                />
              )}
              <div className="col-span-2 min-w-0">
                {labelRow('deliverables_text', 'Deliverables description')}
                <Textarea value={form.deliverables_text} onChange={(e) => set('deliverables_text', e.target.value)} rows={3} />
              </div>
            </Section>

            <Section icon={ShoppingBag} title="Products" divider>
              <div className="col-span-2">
                {labelRow('products', 'Products')}
                <ProductRowsEditor value={form.products} onChange={(next) => set('products', next)} />
              </div>
            </Section>
          </div>
        </CardShell>

        <CardShell icon={FileText} title="Brief">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-x-7 gap-y-5">
              <div className="min-w-0">
                {labelRow('brief.video_title', 'Brief title')}
                <Input value={form.video_title} onChange={(e) => set('video_title', e.target.value)} />
              </div>
              <div className="min-w-0">
                {labelRow('brief.cta', 'Call to action')}
                <Input value={form.cta} onChange={(e) => set('cta', e.target.value)} />
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-caption-medium tracking-wide text-muted-foreground uppercase">
                Description
                {commentDot('description', 'Description')}
              </div>
              <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} />
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-caption-medium tracking-wide text-muted-foreground uppercase">
                Brief description
                {commentDot('brief.video_description', 'Brief description')}
              </div>
              <Textarea value={form.video_description} onChange={(e) => set('video_description', e.target.value)} rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-x-7 gap-y-5">
              <div className="min-w-0">
                {labelRow('brief.primary_focus', 'Primary focus')}
                <Textarea value={form.primary_focus} onChange={(e) => set('primary_focus', e.target.value)} rows={2} />
              </div>
              <div className="min-w-0">
                {labelRow('brief.secondary_focus', 'Secondary focus')}
                <Textarea value={form.secondary_focus} onChange={(e) => set('secondary_focus', e.target.value)} rows={2} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="space-y-2.5">
                <div className="flex items-center gap-1.5 text-body-sm-medium tracking-wide text-emerald-600 uppercase">
                  Do&apos;s
                  {commentDot('brief.dos', "Do's")}
                </div>
                <EditableList values={form.dos} onChange={(v) => set('dos', v)} addLabel="Add a do" />
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-1.5 text-body-sm-medium tracking-wide text-rose-600 uppercase">
                  Don&apos;ts
                  {commentDot('brief.donts', "Don'ts")}
                </div>
                <EditableList values={form.donts} onChange={(v) => set('donts', v)} addLabel="Add a don't" />
              </div>
            </div>

            <ReferencesField
              value={form.sample_video_url}
              onChange={(v) => set('sample_video_url', v)}
              labelExtra={commentDot('brief.sample_video_url', 'References')}
            />
          </div>
        </CardShell>
      </div>

      <Sheet open={commentsOpen} onOpenChange={setCommentsOpen}>
        <SheetContent side="right" className="w-[420px] sm:max-w-[420px]">
          <SheetHeader>
            <SheetTitle>Comments{commentedPaths.length > 0 ? ` (${commentedPaths.length})` : ''}</SheetTitle>
            <p className="text-body-sm-regular text-muted-foreground">
              Notes you left on your changes, for the review team.
            </p>
          </SheetHeader>
          <div className="flex-1 space-y-3 overflow-auto px-4 py-2">
            {commentedPaths.length === 0 && (
              <p className="text-body-sm-regular text-muted-foreground">
                No comments yet — a note box opens each time you change a field.
              </p>
            )}
            {commentedPaths.map((path) => (
              <CommentCard
                key={path}
                path={path}
                change={describeChange(path, getFieldValue(initial, path), getFieldValue(form, path))}
                comment={comments[path] ?? ''}
                commentedAt={commentedAt[path]}
                authorName={authorName}
                editing={editingPath === path}
                onEdit={() => setEditingPath(path)}
                onEditDone={() => setEditingPath(null)}
                onCommentChange={(v) => setComment(path, v)}
                onGoToField={() => {
                  setCommentsOpen(false);
                  window.setTimeout(() => goToField(path), SHEET_CLOSE_ANIMATION_MS);
                }}
              />
            ))}
          </div>
          <SheetFooter>
            <Button onClick={handleSubmit} disabled={!hasChanges || submitting} className="w-full">
              {submitting ? 'Submitting…' : `${submitLabel} (${dirtyPaths.length})`}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
